import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUBJECT_DOT } from "@/lib/constants";
import { ClipboardList, Target, PencilRuler, Clock, Flame, Trophy, Lock, Sparkles, CalendarCheck, Medal, Footprints, BookOpen, Zap, Award, Star } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const BADGE_ICONS = {
  footprints: Footprints, clipboard: ClipboardList, "clipboard-check": Award, book: BookOpen,
  pencil: PencilRuler, trophy: Trophy, zap: Zap, clock: Clock, flame: Flame, target: Target,
};

export default function Progress() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [gamif, setGamif] = useState(null);
  const [planning, setPlanning] = useState(null);
  const [planningLoading, setPlanningLoading] = useState(false);
  const isPremium = user?.plan === "premium";

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/gamification").then(({ data }) => setGamif(data)).catch(() => {});
  }, []);

  const generatePlanning = async () => {
    setPlanningLoading(true);
    try {
      const { data } = await api.post("/ai/planning");
      setPlanning(data);
    } catch {
      setPlanning(null);
    } finally {
      setPlanningLoading(false);
    }
  };

  if (!stats) {
    return <div className="animate-pulse space-y-4" data-testid="progress-loading">
      <div className="h-10 w-64 bg-slate-200 rounded-xl" />
      <div className="grid md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-slate-100 rounded-3xl" />)}</div>
    </div>;
  }

  const hours = Math.floor(stats.total_minutes / 60);
  const mins = stats.total_minutes % 60;

  return (
    <div className="space-y-6" data-testid="progress-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Ma progression</h1>
        <p className="text-slate-500 text-sm sm:text-base mt-1">Tes efforts en chiffres. Chaque minute compte.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Devoirs terminés", value: stats.homework_done, sub: `sur ${stats.homework_total}`, icon: ClipboardList, color: "bg-rose-100 text-rose-600", testid: "progress-homework-done" },
          { label: "Objectifs accomplis", value: stats.goals_done, sub: `sur ${stats.goals_total}`, icon: Target, color: "bg-amber-100 text-amber-600", testid: "progress-goals-done" },
          { label: "Exercices réalisés", value: stats.exercises_done, sub: `score moyen ${stats.avg_score}%`, icon: PencilRuler, color: "bg-sky-100 text-sky-600", testid: "progress-exercises-done" },
          { label: "Temps de révision", value: hours > 0 ? `${hours}h${String(mins).padStart(2, "0")}` : `${mins} min`, sub: "au total", icon: Clock, color: "bg-emerald-100 text-emerald-600", testid: "progress-total-minutes" },
        ].map(({ label, value, sub, icon: Icon, color, testid }) => (
          <div key={testid} data-testid={testid} className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
            <div className={`h-9 w-9 rounded-xl ${color} flex items-center justify-center mb-3`}>
              <Icon className="h-[18px] w-[18px]" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{label} · {sub}</p>
          </div>
        ))}
      </div>

      {gamif && (
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6" data-testid="gamification-section">
          <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-600 p-6 text-white shadow-sm" data-testid="gamification-level">
            <div className="flex items-center gap-2 mb-1">
              <Medal className="h-5 w-5" />
              <p className="text-xs font-bold uppercase tracking-wider text-indigo-100">Niveau {gamif.level}</p>
            </div>
            <p className="text-3xl font-bold font-display">{gamif.xp} XP</p>
            <div className="mt-3">
              <div className="flex justify-between text-[11px] text-indigo-100 mb-1.5">
                <span>Niveau {gamif.level}</span>
                <span data-testid="gamification-progress-label">{gamif.progress}/{gamif.next_level_xp} XP</span>
              </div>
              <div className="h-2.5 rounded-full bg-white/20 overflow-hidden">
                <div className="h-full bg-white rounded-full transition-[width] duration-500" style={{ width: `${(gamif.progress / gamif.next_level_xp) * 100}%` }} />
              </div>
            </div>
            <p className="mt-3 text-xs text-indigo-100 flex items-center gap-1.5">
              <Flame className="h-3.5 w-3.5" /> {gamif.streak} jour{gamif.streak > 1 ? "s" : ""} d'affilée
            </p>
            {gamif.perks && (
              <div className="mt-4 rounded-2xl bg-white/10 p-3.5" data-testid="gamification-perks">
                <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-100 mb-1.5">Récompenses de niveau</p>
                <p className="text-xs text-white font-medium">
                  Actuelles : {gamif.perks.sheet_limit} fiches max · +{gamif.perks.extra_exercises} exercices Basique
                </p>
                <p className="text-[11px] text-indigo-100 mt-1">
                  Niveau {gamif.level + 1} : +2 fiches et +2 exercices Basique en plus !
                </p>
              </div>
            )}
          </div>
          <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="gamification-badges">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-500" /> Mes badges
              </h2>
              <span className="text-xs font-bold text-slate-500" data-testid="gamification-badge-count">{gamif.unlocked_count}/{gamif.badges.length} débloqués</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {gamif.badges.map((b) => {
                const Icon = BADGE_ICONS[b.icon] || Trophy;
                return (
                  <div
                    key={b.key}
                    data-testid={`badge-${b.key}`}
                    title={b.desc}
                    className={`rounded-2xl border p-3 text-center transition-transform duration-200 ${
                      b.unlocked
                        ? "border-amber-200 bg-amber-50 hover:-translate-y-0.5"
                        : "border-slate-100 bg-slate-50/50 opacity-50"
                    }`}
                  >
                    <div className={`h-9 w-9 mx-auto rounded-xl flex items-center justify-center ${b.unlocked ? "bg-amber-400 text-white" : "bg-slate-200 text-slate-400"}`}>
                      <Icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
                    </div>
                    <p className={`mt-2 text-[11px] font-bold leading-tight ${b.unlocked ? "text-slate-900" : "text-slate-400"}`}>{b.name}</p>
                    <p className="text-[9px] text-slate-400 mt-0.5 leading-tight">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Weekly chart */}
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="progress-week-chart">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Temps de travail — 7 derniers jours</h2>
          <p className="text-xs text-slate-500 mb-5">Révisions + exercices, en minutes</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.week}>
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} width={30} />
                <Tooltip cursor={{ fill: "rgba(79,70,229,0.06)" }} formatter={(v) => [`${v} min`, "Temps"]} />
                <Bar dataKey="minutes" radius={[8, 8, 0, 0]}>
                  {stats.week.map((d, i) => (
                    <Cell key={i} fill={d.minutes > 0 ? "#4F46E5" : "#E2E8F0"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* By subject */}
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="progress-by-subject">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Exercices par matière</h2>
          <p className="text-xs text-slate-500 mb-5">Où concentres-tu tes efforts ?</p>
          {stats.by_subject.length === 0 ? (
            <p className="text-sm text-slate-400 py-10 text-center">Réalise ton premier exercice pour voir tes stats par matière.</p>
          ) : (
            <ul className="space-y-4">
              {stats.by_subject.map((s) => {
                const max = Math.max(...stats.by_subject.map((x) => x.exercises));
                return (
                  <li key={s.subject} data-testid={`progress-subject-${s.subject.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="font-medium text-slate-800">{s.subject}</span>
                      <span className="text-slate-500 text-xs font-semibold">{s.exercises} ex · {s.minutes} min</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(s.exercises / max) * 100}%`, backgroundColor: SUBJECT_DOT[s.subject] || "#64748B" }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Premium section */}
      {isPremium ? (
        <div className="space-y-4" data-testid="premium-stats-section">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 p-6 text-white" data-testid="progress-streak">
              <Flame className="h-7 w-7 mb-3" />
              <p className="text-3xl font-bold">{stats.streak_days ?? 0} jour{(stats.streak_days ?? 0) > 1 ? "s" : ""}</p>
              <p className="text-xs text-indigo-100 mt-1">de travail d'affilée (série en cours)</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-white" data-testid="progress-best-score">
              <Trophy className="h-7 w-7 mb-3" />
              <p className="text-3xl font-bold">{stats.best_score ?? 0}%</p>
              <p className="text-xs text-emerald-100 mt-1">meilleur score à un exercice</p>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="ai-planning-section">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-500" /> Planning de révision personnalisé (IA)
              </h2>
              <button onClick={generatePlanning} disabled={planningLoading} data-testid="ai-planning-button" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60">
                {planningLoading ? "Génération…" : planning ? "Régénérer" : "Générer mon planning"}
              </button>
            </div>
            {planning && (
              <div className="animate-fade-in">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {planning.plan.map((p) => (
                    <div key={p.day} data-testid={`ai-planning-day-${p.day.toLowerCase()}`} className="rounded-2xl border border-violet-100 bg-violet-50/40 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-900">{p.day}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 flex items-center gap-1">
                          <CalendarCheck className="h-3 w-3" /> {p.duration} min
                        </span>
                      </div>
                      <p className="text-xs font-semibold text-indigo-700">{p.subject}</p>
                      <p className="text-xs text-slate-600 mt-1">{p.task}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-500 italic" data-testid="ai-planning-tip">{planning.tip}</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="relative rounded-3xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 p-8 overflow-hidden" data-testid="premium-locked-section">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shrink-0">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900">Stats détaillées & planning IA — réservés au Premium</h2>
              <p className="text-sm text-slate-600 mt-1">Séries de révision, meilleur score, planning personnalisé par IA… Passe à Premium pour débloquer ton coach personnel.</p>
            </div>
            <Link to="/pricing" data-testid="premium-upgrade-cta" className="px-6 py-3 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold hover:opacity-90 whitespace-nowrap">
              Découvrir Premium
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
